import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import NotificationsIcon from "@mui/icons-material/NotificationsRounded";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  getUserNotifications,
  markAllNotificationRead,
  markNotificationRead,
  normalizeDate,
} from "../../utilities";
import { selectRealUser, selectUser } from "../../features/userSlice";

const POLL_MS = 60_000;

// Header bell: unread count badge + dropdown of notifications. Reads from
// /api/notifications/getUserNotifications; rows are removed by marking them
// read (there is no read-history view client-side).
const NotificationButton = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const rootRef = useRef(null);
  const user = useSelector(selectUser);
  // The id only records presence (last_seen/last_route). During an admin
  // preview it must stay the admin's, or the previewed person shows as
  // online on the admin's page.
  const presenceId = useSelector(selectRealUser)?.id;

  useEffect(() => {
    if (!user?.email) return;
    const load = () =>
      getUserNotifications(
        setNotifications,
        user.email,
        presenceId,
        window.location.pathname
      );
    load();
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [user?.email, presenceId]);

  // Close the dropdown on any click outside it.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setShowPopup(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const dismiss = (id) => {
    setNotifications((prev) => prev.filter((n) => n._id !== id));
    markNotificationRead(id);
  };

  const dismissAll = () => {
    markAllNotificationRead(user.email);
    setNotifications([]);
    setShowPopup(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setShowPopup((prev) => !prev)}
        aria-label={`Notifications${
          notifications.length ? ` (${notifications.length} unread)` : ""
        }`}
        className="app-icon-btn relative"
      >
        <NotificationsIcon fontSize="small" />
        {notifications.length > 0 && (
          <span className="absolute right-0.5 top-0.5 flex h-[1.1rem] min-w-[1.1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow-sm ring-2 ring-white dark:ring-slate-900">
            {notifications.length > 9 ? "9+" : notifications.length}
          </span>
        )}
      </button>

      {showPopup && (
        <div className="app-glass app-glass-rim absolute -right-20 top-12 z-50 max-h-[28rem] w-[calc(100vw-2rem)] max-w-sm overflow-y-auto rounded-2xl border p-2 sm:right-0">
          <div className="mb-1 flex items-center justify-between px-2 pb-1 pt-1">
            <p className="font-semibold">Notifications</p>
            {notifications.length > 0 && (
              <button
                onClick={dismissAll}
                className="app-link rounded-full px-2 py-1 text-xs"
              >
                Mark all as read
              </button>
            )}
          </div>

          {notifications.length > 0 ? (
            notifications.map((item) => (
              <div
                key={item._id}
                className="mb-1 rounded-xl p-3 transition-colors last:mb-0 hover:bg-[color:var(--app-hover)]"
              >
                <div className="app-muted flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-medium">{item.from}</span>
                  <span className="shrink-0">{normalizeDate(item.date)}</span>
                </div>
                <div className="mt-1 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{item.subject}</p>
                    <p className="app-muted text-sm">{item.message}</p>
                    {item.redirect && (
                      <Link
                        to={item.redirect}
                        onClick={() => {
                          dismiss(item._id);
                          setShowPopup(false);
                        }}
                        className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-sky-600 hover:underline dark:text-sky-400"
                      >
                        Open
                        <ArrowForwardIcon sx={{ fontSize: 14 }} />
                      </Link>
                    )}
                  </div>
                  <button
                    onClick={() => dismiss(item._id)}
                    aria-label="Mark notification as read"
                    className="app-muted shrink-0 rounded-full p-1 transition hover:bg-[color:var(--app-pressed)] hover:text-[color:var(--app-text)]"
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center px-4 py-8 text-center">
              <span className="app-icon-tile mb-3 bg-slate-500/10 text-slate-500 dark:text-slate-400">
                <NotificationsIcon />
              </span>
              <p className="text-sm font-semibold">You're all caught up</p>
              <p className="app-muted mt-0.5 text-xs">
                New notifications will show up here.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationButton;
